import React, { useState, useEffect, useCallback } from 'react';
import CvManager from './CvManager.js';
import './CvManagementPage.css';

const CvManagementPage = () => {
  const [selectedCv, setSelectedCv] = useState('default');
  const [isEditing, setIsEditing] = useState(false);
  const [cvs, setCvs] = useState([]);
  const [cvsLoading, setCvsLoading] = useState(true);
  const [cvsError, setCvsError] = useState(null);

  const loadCvs = useCallback(async () => {
    setCvsLoading(true);
    setCvsError(null);
    try {
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8888/.netlify/functions/cv-list'
        : '/.netlify/functions/cv-list';
      const response = await fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        setCvs(data.cvs || []);
      } else {
        throw new Error('Failed to load CVs');
      }
    } catch (error) {
      setCvsError(error.message);
    } finally {
      setCvsLoading(false);
    }
  }, []);

  // Get current CV from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cvName = urlParams.get('name') || 'default';
    setSelectedCv(cvName);
  }, []);

  useEffect(() => {
    loadCvs();
  }, [loadCvs]);

  const handleCvSelect = (cvName) => {
    setSelectedCv(cvName);
    // Update URL without page reload
    const url = new URL(window.location);
    if (cvName === 'default') {
      url.searchParams.delete('name');
    } else {
      url.searchParams.set('name', cvName);
    }
    window.history.pushState({}, '', url);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const getCvDisplayName = (cvName) => {
    return cvName === 'default' ? 'Eli Elhadad (Default)' : cvName;
  };

  return (
    <div className="cv-management-page">
      <div className="cv-management-header">
        <div className="header-content">
          <div className="header-left"></div>
          <div className="header-center">
            <h1>CV Management</h1>
            <p>Manage multiple CVs and switch between them for editing</p>
          </div>
          <div className="header-actions">
            <button
              onClick={handleEditToggle}
              className={`edit-toggle-button ${isEditing ? 'editing' : ''}`}
            >
              <i className={`fas ${isEditing ? 'fa-save' : 'fa-edit'}`}></i>
              {isEditing ? 'Exit Edit Mode' : 'Edit Mode'}
            </button>
          </div>
        </div>
      </div>

      <div className="cv-management-content">
        <div className="cv-selection-panel">
          <h2>Select CV to Manage</h2>
          <div className="cv-list">
            <CvSelector
              cvs={cvs}
              loading={cvsLoading}
              error={cvsError}
              loadCvs={loadCvs}
              selectedCv={selectedCv}
              onCvSelect={handleCvSelect}
              isEditing={isEditing}
            />
          </div>
        </div>

        <div className="cv-management-panel">
          <div className="current-cv-header">
            <h2>Managing: {getCvDisplayName(selectedCv)}</h2>
            <div className="cv-actions">
              <a 
                href={`/?name=${selectedCv === 'default' ? '' : selectedCv}`}
                target="_blank"
                rel="noopener noreferrer"
                className="view-cv-button"
              >
                <i className="fas fa-external-link-alt"></i>
                View CV
              </a>
              <a 
                href={`/?name=${selectedCv === 'default' ? '' : selectedCv}&admin=true`}
                className="edit-cv-button"
              >
                <i className="fas fa-edit"></i>
                Edit CV Data
              </a>
            </div>
          </div>
          
          <CvManager cvs={cvs} loading={cvsLoading} loadCvs={loadCvs} isEditing={isEditing} />
        </div>
      </div>
    </div>
  );
};

// CV Selector Component
const CvSelector = ({ cvs, loading, error, loadCvs, selectedCv, onCvSelect }) => {
  if (loading) {
    return (
      <div className="cv-selector-loading">
        <div className="loading-spinner"></div>
        <p>Loading CVs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cv-selector-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>Error loading CVs: {error}</p>
        <button onClick={loadCvs} className="retry-button">
          <i className="fas fa-redo"></i>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="cv-selector">
      {cvs.map((cv) => (
        <div
          key={cv.name}
          className={`cv-option ${selectedCv === cv.name ? 'selected' : ''}`}
          onClick={() => onCvSelect(cv.name)}
        >
          <div className="cv-option-content">
            <div className="cv-option-header">
              <h3>{cv.name === 'default' ? 'Eli Elhadad' : cv.name}</h3>
              <span className="cv-status">
                {cv.name === 'default' ? 'Default CV' : 'Custom CV'}
              </span>
            </div>
            <p className="cv-title">{cv.title}</p>
            <div className="cv-meta">
              <span>Created: {new Date(cv.created_at).toLocaleDateString()}</span>
              {cv.updated_at && (
                <span>Updated: {new Date(cv.updated_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
          <div className="cv-option-actions">
            <i className="fas fa-chevron-right"></i>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CvManagementPage;
